import React, { useEffect, useMemo } from 'react';
import { browserHistory } from 'react-router';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import cn from 'classnames';
import size from 'lodash/size';

import {
  useEmergencyFormState,
  validateRequiredFields,
} from './useEmergencyFormState';
import { usePatientValidation } from '../PatientRegistrationInfo/usePatientForm';
import toSnakeCaseObjectKeys from '../../../utils/toSnakeCaseObjectKeys';
import { EMERGENCY_CONTACT_DATA_KEY } from '../../../constants/metadata';
import { PROFILE_FIELDS_TYPES } from '../../../constants/formFields';
import { PATIENT_REGISTRATION } from '../../../constants/routes';
import { usePageRedirect } from '../utils/usePageRedirect';
import { DIALOG_TITLE } from '../../../constants/strings';
import deepTrim from '../../../utils/converters/deepTrim';
import EmergencyContactForm from './EmergencyContactForm';
import { getSystemError } from '../utils/convertErrors';
import { BREAKPOINT } from '../../../constants/styles';
import { EMPTY_ROLE } from '../../../constants';
import BackButton from '../../common/BackButton';
import ErrorBlock from '../../common/ErrorBlock';
import Loader from '../../common/Loader';
import Button from '../../Button';
import { enhance } from './enhance';

const PatientEmergencyContact = ({
  clearRequest,
  personalInfo,
  formatMessage,
  createPatient,
  patientAddress,
  patientContacts,
  clearPatientData,
  isAddPatientSuccess,
  validationError: errors,
  validationPending: pending,
  patientProfileAlreadyExists,
  emergencyContactPatientList,
}) => {
  const {
    emergencyState,
    handleEmergencyFormChange,
    clearEmergencyForm,
  } = useEmergencyFormState({ [PROFILE_FIELDS_TYPES.CARE_ROLE]: EMPTY_ROLE });

  useEffect(() => {
    const success =
      isAddPatientSuccess &&
      (!patientProfileAlreadyExists ||
        (patientProfileAlreadyExists &&
          size(emergencyContactPatientList) !== 2));

    if (success) {
      clearEmergencyForm();
      clearPatientData();
      clearRequest();
      browserHistory.push(PATIENT_REGISTRATION);
    }
  }, [
    isAddPatientSuccess,
    patientProfileAlreadyExists,
    emergencyContactPatientList,
  ]);

  usePageRedirect(PATIENT_REGISTRATION, [
    patientContacts,
    personalInfo,
    patientAddress,
  ]);

  const { validateErrors, clearErrors } = usePatientValidation(errors);

  const isEmpty = useMemo(() => {
    const { careRole, ...other } = emergencyState;
    return (
      careRole === EMPTY_ROLE &&
      Object.keys(other).every(key => !emergencyState[key])
    );
  }, [emergencyState]);

  const emergencyData = useMemo(() => {
    const { careRole, ...other } = emergencyState;
    const hasCareRole = careRole !== EMPTY_ROLE;
    const data = hasCareRole
      ? {
          careRole,
          ...other,
        }
      : { ...other };

    return isEmpty ? {} : toSnakeCaseObjectKeys(data);
  }, [emergencyState, isEmpty]);

  const isValid = validateRequiredFields(emergencyState);

  const systemError = getSystemError(errors);

  const handleBack = () => {
    browserHistory.goBack();
  };

  const handleSubmit = e => {
    e.preventDefault();

    const patientInfoData = toSnakeCaseObjectKeys({
      ...patientContacts,
      ...personalInfo,
      ...patientAddress,
    });

    const patientData = {
      ...patientInfoData,
      [EMERGENCY_CONTACT_DATA_KEY]: emergencyData,
    };

    createPatient(deepTrim(patientData));
  };

  const handleClear = () => {
    clearEmergencyForm();
    clearRequest();
    clearErrors();
  };

  return (
    <div className="Root">
      <div className="g-content" styleName="root">
        <div styleName="header">
          <MediaQuery minWidth={BREAKPOINT}>
            <BackButton onBack={handleBack} />
          </MediaQuery>
        </div>
        <If condition={pending}>
          <Loader className="g-centeredAll" />
        </If>
        <div styleName={cn('content', { disabled: pending })}>
          <form onSubmit={handleSubmit} styleName="form">
            <label styleName="form--label" className="mb3">
              {formatMessage(DIALOG_TITLE.addPatientTitle)}
            </label>
            <p styleName="form--text" className="mb4">
              {formatMessage({ id: 'dialog.titles.emergencyContactRole' })}
            </p>
            <span styleName="form--subtitle">
              {formatMessage({ id: 'dialog.titles.emergencyContact' })}
            </span>
            {systemError && <ErrorBlock errors={systemError} className="mt3" />}
            <EmergencyContactForm
              {...emergencyState}
              clearRequest={clearRequest}
              formatMessage={formatMessage}
              onChange={handleEmergencyFormChange}
              validateErrors={validateErrors}
            />
            <div styleName="form--btn" className="mt4">
              <Button
                className="mr3"
                type="submit"
                raised
                color="primary"
                disabled={pending || (isEmpty ? false : !isValid)}
              >
                {formatMessage({ id: 'common.complete' })}
              </Button>
              <Button raised color="accent" onClick={handleClear} type="reset">
                {formatMessage({ id: 'common.clear' })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PatientEmergencyContact.propTypes = {
  formatMessage: PropTypes.func,
  createPatient: PropTypes.func,
  clearPatientData: PropTypes.func,
  clearRequest: PropTypes.func,
  personalInfo: PropTypes.object,
  patientAddress: PropTypes.object,
  patientContacts: PropTypes.object,
  isAddPatientSuccess: PropTypes.bool,
  validationError: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  validationPending: PropTypes.bool,
  patientProfileAlreadyExists: PropTypes.bool,
  emergencyContactPatientList: PropTypes.array,
};

export default enhance(PatientEmergencyContact);



// WEBPACK FOOTER //
// ./src/components/PatientRegistration/PatientEmergencyContact/index.jsx